import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {LocalstorageService} from '../../../@services/localstorage.service';
import {NotificationService} from '../../../@services/notification.service';
import {PosItemService} from '../../../@services/pos-item.service';
import {UserShiftService} from '../../../@services/user-shift.service';

interface WastageDialogData {
  shiftId?: number | null;
  wastage?: any;
}

@Component({
  selector: 'app-wastage-dialog',
  templateUrl: './wastage-dialog.component.html',
  styleUrls: ['./wastage-dialog.component.scss']
})
export class WastageDialogComponent implements OnInit {

  title = 'Quick Add Wastage';
  merchant: any;
  shiftId: number | null = null;
  editId: number | null = null;
  readonly quickReasons = [
    'Damaged item',
    'Expired item',
    'Spoiled during prep',
    'Wrong order',
    'Quality issue',
  ];

  itemOptions: any[] = [];
  itemPriceById = new Map<number, number>();

  form = {
    posItemVsMerchantId: null,
    quantity: 1,
    reason: '',
    remark: '',
    occurredAt: new Date(),
  };
  quantityInput = '1';

  unitPrice = 0;
  totalAmount = 0;
  saving = false;

  get saveDisabled(): boolean {
    const quantity = Number(this.form.quantity);
    return this.saving
      || !this.form.posItemVsMerchantId
      || !Number.isFinite(quantity)
      || quantity <= 0;
  }

  get itemLocked(): boolean {
    return !!this.editId;
  }

  get canDecreaseQty(): boolean {
    const quantity = Number(this.form.quantity);
    return Number.isFinite(quantity) && quantity > 1;
  }

  get reasonOptions(): string[] {
    const current = (this.form.reason || '').trim();
    return current && !this.quickReasons.includes(current)
      ? [current, ...this.quickReasons]
      : this.quickReasons;
  }

  constructor(
    private http: HttpClient,
    private dialogRef: MatDialogRef<WastageDialogComponent>,
    private ls: LocalstorageService,
    private ns: NotificationService,
    private posItemService: PosItemService,
    private userShiftService: UserShiftService,
    @Inject(MAT_DIALOG_DATA) public data: WastageDialogData,
  ) {
    this.merchant = this.ls.getMerchant();
  }

  ngOnInit(): void {
    const shift = this.userShiftService.getUserShift();
    this.shiftId = this.data?.shiftId || shift?.id || null;

    if (this.data?.wastage?.id) {
      this.editId = this.data.wastage.id;
      this.title = 'Edit Wastage';
      this.form = {
        posItemVsMerchantId: this.data.wastage?.posItemVsMerchant?.id || null,
        quantity: Number(this.data.wastage?.quantity || 1),
        reason: this.data.wastage?.reason || '',
        remark: this.data.wastage?.remark || '',
        occurredAt: this.data.wastage?.occurredAt ? new Date(this.data.wastage.occurredAt) : new Date(),
      };
    }
    this.normalizeQuantity(this.form.quantity);

    this.posItemService.getCachePagedPosItemVsMerchant(this.merchant.id).subscribe((res) => {
      const content = res?.content || [];
      this.itemOptions = content
        .map((item) => ({
          id: item.id,
          name: item?.posItem?.name || `Item #${item.id}`,
          price: Number(item?.price || 0),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      content.forEach((item) => {
        this.itemPriceById.set(item.id, Number(item?.price || 0));
      });
      this.recalculateTotal();
    });
  }

  onItemChange() {
    this.recalculateTotal();
  }

  onQuantityChange() {
    this.recalculateTotal();
  }

  onQuantityInput(value: string) {
    const cleaned = (value || '').replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
    this.quantityInput = cleaned;
    if (!cleaned) {
      this.form.quantity = null;
      this.recalculateTotal();
      return;
    }

    const parsed = Number(cleaned);
    this.form.quantity = Number.isFinite(parsed) ? parsed : null;
    this.recalculateTotal();
  }

  normalizeQuantity(value?: unknown) {
    const parsed = Number(value ?? this.quantityInput ?? this.form.quantity);
    const normalized = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    this.form.quantity = normalized;
    this.quantityInput = normalized.toString();
    this.recalculateTotal();
  }

  decreaseQty() {
    this.normalizeQuantity();
    const currentQty = Number(this.form.quantity || 1);
    this.form.quantity = Math.max(1, currentQty - 1);
    this.quantityInput = this.form.quantity.toString();
    this.onQuantityChange();
  }

  increaseQty() {
    this.normalizeQuantity();
    const currentQty = Number(this.form.quantity || 1);
    this.form.quantity = currentQty + 1;
    this.quantityInput = this.form.quantity.toString();
    this.onQuantityChange();
  }

  trackByItemId(_: number, item: any): number {
    return item.id;
  }

  onReasonChange(reason: string) {
    this.form.reason = reason || '';
  }

  private recalculateTotal() {
    const itemId = Number(this.form.posItemVsMerchantId || 0);
    this.unitPrice = this.itemPriceById.get(itemId) || 0;
    const quantity = Math.max(0, Number(this.form.quantity || 0));
    this.totalAmount = quantity * this.unitPrice;
  }

  private valid(): boolean {
    if (!this.shiftId) {
      this.ns.showNotification({message: 'No active shift found. Please start shift first.'});
      return false;
    }

    if (this.editId && this.data?.wastage?.userShift?.id && this.data.wastage.userShift.id !== this.shiftId) {
      this.ns.showNotification({message: 'Only current shift wastage can be edited in POS.'});
      return false;
    }

    if (!this.form.posItemVsMerchantId) {
      this.ns.showNotification({message: 'Please select an item.'});
      return false;
    }

    if (!this.form.quantity || Number(this.form.quantity) <= 0) {
      this.ns.showNotification({message: 'Quantity must be more than 0.'});
      return false;
    }

    return true;
  }

  save() {
    if (!this.valid() || this.saving) {
      return;
    }

    const reason = (this.form.reason || '').trim() || null;
    this.saving = true;
    const payload = {
      merchantId: this.merchant.id,
      wastage: {
        id: this.editId,
        posItemVsMerchant: {
          id: Number(this.form.posItemVsMerchantId),
        },
        userShift: {
          id: this.shiftId,
        },
        quantity: Number(this.form.quantity),
        reason,
        remark: (this.form.remark || '').trim(),
        occurredAt: this.form.occurredAt || new Date(),
        source: 'DPOS_POS_UI',
      },
    };

    this.http.post<any>(`${environment.apiUrl}/api/wastage/upsertWastage`, payload).subscribe(
      (res) => {
        this.saving = false;
        this.ns.showNotification({message: this.editId ? 'Wastage updated successfully' : 'Wastage recorded successfully'});
        this.dialogRef.close(res);
      },
      (error) => {
        this.saving = false;
        this.ns.showNotification({message: error?.error?.message || 'Failed to save wastage'});
      }
    );
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
